/**
 * Shared table for the two bare-array invoice feeds (`payments/upcoming/`,
 * `payments/overdue/`). Both return `InvoiceAsPayment[]` directly, NOT the
 * `Paginated<T>` envelope every other list endpoint uses — no page controls
 * here, the whole feed comes back in one response.
 */
import { useQuery } from '@tanstack/react-query';
import { parseApiError } from '../../lib/apiError';
import { useIsMobile } from '../../lib/useIsMobile';
import type { InvoiceAsPayment } from '../../types/payment';

export interface PaymentFeedPageProps {
  title: string;
  queryKey: string;
  queryFn: () => Promise<InvoiceAsPayment[]>;
  emptyMessage: string;
}

export function PaymentFeedPage({
  title,
  queryKey,
  queryFn,
  emptyMessage,
}: PaymentFeedPageProps): React.JSX.Element {
  const isMobile = useIsMobile();
  const query = useQuery({ queryKey: [queryKey], queryFn });
  const invoices = query.data ?? [];

  return (
    <section>
      <h2>{title}</h2>

      {query.isPending && <p>Loading…</p>}
      {query.isError && (
        <p role="alert" className="form__error">
          {parseApiError(query.error).message}
        </p>
      )}

      {query.isSuccess && invoices.length === 0 && <p>{emptyMessage}</p>}

      {query.isSuccess && invoices.length > 0 && (
        isMobile ? (
          <ul className="record-cards">
            {invoices.map((invoice) => (
              <li key={invoice.id} className="record-card">
                <div className="record-card__row">
                  <span className="record-card__label">Tenant</span>
                  <span className="record-card__value">{invoice.tenant_name}</span>
                </div>
                <div className="record-card__row">
                  <span className="record-card__label">Unit</span>
                  <span className="record-card__value">{invoice.unit_number}</span>
                </div>
                <div className="record-card__row">
                  <span className="record-card__label">Property</span>
                  <span className="record-card__value">{invoice.property_name}</span>
                </div>
                <div className="record-card__row">
                  <span className="record-card__label">Charge type</span>
                  <span className="record-card__value">{invoice.charge_type}</span>
                </div>
                <div className="record-card__row">
                  <span className="record-card__label">Balance</span>
                  <span className="record-card__value">{invoice.amount}</span>
                </div>
                <div className="record-card__row">
                  <span className="record-card__label">Due date</span>
                  <span className="record-card__value">{invoice.due_date}</span>
                </div>
                <div className="record-card__row">
                  <span className="record-card__label">Status</span>
                  <span className="record-card__value">{invoice.status}</span>
                </div>
                <div className="record-card__row">
                  <span className="record-card__label">Overdue</span>
                  <span className="record-card__value">{invoice.is_overdue ? 'Yes' : 'No'}</span>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Tenant</th>
                <th>Unit</th>
                <th>Property</th>
                <th>Charge type</th>
                <th>Balance</th>
                <th>Due date</th>
                <th>Status</th>
                <th>Overdue</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((invoice) => (
                <tr key={invoice.id}>
                  <td>{invoice.tenant_name}</td>
                  <td>{invoice.unit_number}</td>
                  <td>{invoice.property_name}</td>
                  <td>{invoice.charge_type}</td>
                  <td>{invoice.amount}</td>
                  <td>{invoice.due_date}</td>
                  <td>{invoice.status}</td>
                  <td>{invoice.is_overdue ? 'Yes' : 'No'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      )}
    </section>
  );
}

export default PaymentFeedPage;
